import React, { useMemo, useState } from 'react';

type ActivityConfig = {
  activity: string;
  subActivities: string[];
};

type SelectedState = {
  activities: Set<string>;
  subActivities: Set<string>; // stored as "Activity::SubActivity" keys
};

function makeSubKey(activity: string, sub: string) {
  return `${activity}::${sub}`;
}

function parseSubKey(key: string) {
  const [activity, sub] = key.split('::');
  return { activity, sub };
}

export function ActivityPicker({
  items,
  allowSubSelectionWithoutParent = false,
  onChange
}: {
  items: ActivityConfig[];
  /** If true, sub-activities can be toggled even if parent isn't selected */
  allowSubSelectionWithoutParent?: boolean;
  /** Optional callback whenever selection changes */
  onChange?: (selected: {
    activities: string[];
    subActivities: { activity: string; sub: string }[];
  }) => void;
}) {
  const [selected, setSelected] = useState<SelectedState>(() => ({
    activities: new Set<string>(),
    subActivities: new Set<string>()
  }));

  // Make sure duplicates dont cause weirdness
  const normalized = useMemo(() => {
    const seen = new Set<string>();
    return items.filter((x) => {
      if (seen.has(x.activity)) return false;
      seen.add(x.activity);
      return true;
    });
  }, [items]);

  const emitChange = (next: SelectedState) => {
    onChange?.({
      activities: Array.from(next.activities),
      subActivities: Array.from(next.subActivities).map(parseSubKey)
    });
  };

  const toggleActivity = (activity: string) => {
    setSelected((prev) => {
      const next: SelectedState = {
        activities: new Set(prev.activities),
        subActivities: new Set(prev.subActivities)
      };

      const wasSelected = next.activities.has(activity);

      if (wasSelected) {
        // Turning off parent: also clear any sub-activities under it
        next.activities.delete(activity);
        for (const key of Array.from(next.subActivities)) {
          if (key.startsWith(`${activity}::`)) next.subActivities.delete(key);
        }
      } else {
        next.activities.add(activity);
      }

      emitChange(next);
      return next;
    });
  };

  const toggleSubActivity = (activity: string, sub: string) => {
    setSelected((prev) => {
      const next: SelectedState = {
        activities: new Set(prev.activities),
        subActivities: new Set(prev.subActivities)
      };

      const parentSelected = next.activities.has(activity);
      if (!allowSubSelectionWithoutParent && !parentSelected) {
        // ignore click when parent not selected
        return prev;
      }

      const key = makeSubKey(activity, sub);
      if (next.subActivities.has(key)) next.subActivities.delete(key);
      else next.subActivities.add(key);

      emitChange(next);
      return next;
    });
  };

  const clearAll = () => {
    setSelected(() => {
      const next: SelectedState = {
        activities: new Set(),
        subActivities: new Set()
      };
      emitChange(next);
      return next;
    });
  };

  const selectedActivities = normalized.filter(({ activity }) =>
    selected.activities.has(activity)
  );
  const unselectedActivities = normalized.filter(
    ({ activity }) => !selected.activities.has(activity)
  );

  return (
    <div className="max-w-2xl font-sans">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="m-0 text-lg font-semibold">Activities</h3>
        <button
          type="button"
          onClick={clearAll}
          className="ml-auto rounded-md border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Clear
        </button>
      </div>

      {/* Selected main buttons (full width) */}
      {selectedActivities.length > 0 && (
        <div className="mb-4 flex flex-col gap-3">
          {selectedActivities.map(({ activity, subActivities }) => {
            const isSelected = selected.activities.has(activity);
            return (
              <div key={activity} className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => toggleActivity(activity)}
                  aria-pressed={isSelected}
                  className="w-full rounded-lg border border-slate-900 bg-slate-900 px-4 py-2 text-left text-sm font-semibold text-white"
                >
                  {activity}
                  <span className="ml-2 text-xs font-medium text-slate-200">
                    ({subActivities.length})
                  </span>
                </button>

                {/* Sub buttons show only when parent selected */}
                {subActivities.length > 0 && (
                  <div className="flex flex-wrap gap-2 pl-2">
                    {subActivities.map((sub) => {
                      const key = makeSubKey(activity, sub);
                      const subSelected = selected.subActivities.has(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleSubActivity(activity, sub)}
                          aria-pressed={subSelected}
                          className={
                            subSelected
                              ? 'rounded-full border border-blue-600 bg-blue-600 px-3 py-1 text-xs font-semibold text-white'
                              : 'rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-900 hover:bg-slate-200'
                          }
                        >
                          {sub}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Unselected main buttons */}
      <div className="flex flex-wrap gap-3">
        {unselectedActivities.map(({ activity, subActivities }) => {
          const isSelected = selected.activities.has(activity);
          return (
            <button
              key={activity}
              type="button"
              onClick={() => toggleActivity(activity)}
              aria-pressed={isSelected}
              className="min-w-[180px] rounded-lg border border-slate-300 bg-white px-4 py-2 text-left text-sm font-semibold text-slate-900 hover:bg-slate-50"
            >
              {activity}
              <span className="ml-2 text-xs font-medium text-slate-500">
                ({subActivities.length})
              </span>
            </button>
          );
        })}
      </div>

      {/* Debug / display current selection */}
      {/* <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3">
        <div className="mb-2 text-sm font-bold text-slate-900">Selected</div>
        <div className="mb-1 text-sm text-slate-700">
          <span className="font-semibold">Activities:</span>{' '}
          {Array.from(selected.activities).join(', ') || ''}
        </div>
        <div className="text-sm text-slate-700">
          <span className="font-semibold">Sub-activities:</span>{' '}
          {Array.from(selected.subActivities)
            .map((k) => {
              const { activity, sub } = parseSubKey(k);
              return `${activity} 92 ${sub}`;
            })
            .join(', ') || ''}
        </div>
      </div> */}
    </div>
  );
}

/** Example usage */
export function ExampleJSA() {
  const items: ActivityConfig[] = [
    {
      activity: 'Excavation',
      subActivities: ['< 3 feet', '3 - 10 Feet', '> 10 Feet']
    },
    {
      activity: 'Scaffolding',
      subActivities: ['Erection', 'Inspection', 'Dismantle']
    },
    {
      activity: 'Scaffolding-B',
      subActivities: ['Erection', 'Inspection', 'Dismantle']
    }
  ];

  return (
    <div className="w-full flex flex-col items-center">
      <div>
        <p className="text-3xl p-8">JSA Example</p>
      </div>
      <div className="max-w-lg">
        <ActivityPicker
          items={items}
          onChange={(sel) => {
            // You can store this in parent state, send to API, etc.
            console.log('selection', sel);
          }}
        />
        <div className="w-full flex justify-end p-4">
          <button className="ml-auto rounded-md border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Generate JSA
          </button>
        </div>
      </div>
    </div>
  );
}
